import { useEffect, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid2,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, CloudSync } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Cdn, CdnFormErrors, CdnPayload, emptyCdn } from "../types";
import { useModalStyles } from "../../components/modalStyles";
import { validateCdn } from "../validation";

interface ModalCdnProps {
  open: boolean;
  loading?: boolean;
  cdnData?: Cdn | null;
  onClose: () => void;
  onSave: (payload: CdnPayload, id?: number) => void;
}

export default function ModalCdn(props: ModalCdnProps) {
  const { open, loading = false, cdnData, onClose, onSave } = props;

  const [form, setForm] = useState<CdnPayload>(emptyCdn);
  const [errors, setErrors] = useState<CdnFormErrors>({});

  const theme = useTheme();
  const styles = useModalStyles(theme);
  const isEdit = Boolean(cdnData?.id);

  useEffect(() => {
    if (!open) return;

    let initialData: CdnPayload = emptyCdn;

    if (cdnData) {
      const { id, ...rest } = cdnData;
      initialData = { ...emptyCdn, ...rest };
    }

    setForm(initialData);
    setErrors(validateCdn(initialData));
  }, [open, cdnData]);

  const handleChange = (field: keyof CdnPayload, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setErrors(validateCdn(next));
  };

  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = () => {
    const errs = validateCdn(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    onSave(
      {
        as_num: form.as_num.toString().trim(),
        as_name: form.as_name.trim(),
        tipo: form.tipo.trim(),
        bloco: form.bloco.trim(),
      },
      cdnData?.id,
    );
  };

  const fields: {
    field: keyof CdnPayload;
    label: string;
    placeholder: string;
    size: { xs: number; sm: number };
  }[] = [
    {
      field: "as_name",
      label: "Nome",
      placeholder: "Digite o nome do CDN",
      size: { xs: 12, sm: 12 },
    },
    {
      field: "as_num",
      label: "ASN",
      placeholder: "Digite o número do ASN",
      size: { xs: 12, sm: 6 },
    },
    {
      field: "tipo",
      label: "Tipo",
      placeholder: "Digite o tipo",
      size: { xs: 12, sm: 6 },
    },
    {
      field: "bloco",
      label: "Bloco",
      placeholder: "Digite o bloco",
      size: { xs: 12, sm: 12 },
    },
  ];

  return (
    <Modal open={open} onClose={loading ? () => {} : onClose}>
      <Paper sx={{ ...styles.paper, minHeight: "fit-content" }}>
        <Stack sx={styles.boxHeader}>
          <Stack sx={styles.boxTitle}>
            <Stack
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloudSync color="primary" />
            </Stack>
            <Stack>
              <Typography
                fontWeight="bold"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                {isEdit ? "Editar CDN" : "Adicionar CDN"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {form.as_name || "Novo CDN"}
              </Typography>
            </Stack>
          </Stack>
          <IconButton aria-label="close" onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Stack>

        <Divider sx={styles.divider} />

        <Grid2 container columnSpacing={1} sx={styles.boxFields}>
          {fields.map(({ field, label, placeholder, size }) => (
            <Grid2 size={size} key={field}>
              <Typography variant="subtitle2">{label}</Typography>
              <TextField
                fullWidth
                size="small"
                autoComplete="off"
                placeholder={placeholder}
                value={form[field] ?? ""}
                onChange={(event) => handleChange(field, event.target.value)}
                error={Boolean(errors[field])}
                helperText={errors[field] ?? " "}
              />
            </Grid2>
          ))}
        </Grid2>

        <Box sx={styles.boxActions}>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={hasErrors || loading}
            onClick={handleSave}
          >
            {isEdit ? "Salvar" : "Adicionar"}
          </LoadingButton>
        </Box>
      </Paper>
    </Modal>
  );
}
